import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["value"]

import { ref, watch, onMounted, nextTick } from 'vue'
import { loadStripe, Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js'
import { readJsonResponse } from '../../utils/utils'


export default /*@__PURE__*/_defineComponent({
  __name: 'StripeElementCardPay',
  props: {
    mail: { default: '' },
    purchasePrice: { default: 0 },
    btn: { default: 'カードを変更する' },
    title: { default: 'Edbase[エドベース]' },
    description: { default: '' },
    premiumServiceId: {},
    user_id: {},
    pricePlanId: { default: '' },
    couponCode: { default: '' },
    installmentCount: { default: '1' },
    cardComplete: { type: [Function, null], default: null },
    changeFromSubscriptionId: { default: null },
    type_change: { default: '' }
  },
  emits: ["payment-method-cart-setup", "client-secret-cart-setup", "subscription-id-cart-setup", "card-details"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Typed properties for Stripe
const showModal = ref(false)
const isCardComplete = ref(false)
const cardError = ref('')
const isProcessing = ref(false)

// card details
const cardBrand = ref('')
const cardLast4 = ref('')
const cardExpMonth = ref<number | null>(null)
const cardExpYear = ref<number | null>(null)

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let cardElement: StripeCardElement | null = null
let stripePromise: Promise<Stripe | null> | null = null

onMounted(() => {
  initStripePromise()
})

// Initialize Stripe with the official package
const initStripePromise = () => {
  // Try to get key from window global (set by Rails)
  const stripeKey = typeof window !== 'undefined' && 'stripe_publishable_key' in window 
    ? (window as any).stripe_publishable_key
    : 'pk_test_51JTdp5BLyLEisqmg7jI53Eu2dMWbssUIo4Bcs69zI5ZybYLbAVJCwT5IuIYC6I9qzsIAFtyhnH1ReufLLPoBJMbM001cxeSv9D'
  
  // Create the Stripe promise for lazy loading
  stripePromise = loadStripe(stripeKey)
}

watch(showModal, async (isVisible) => {
  if (isVisible) {
    // Wait for DOM to update then initialize Stripe elements
    await nextTick()
    await initializeStripeElements()
  } else {
    unmountCardElement()
  }
})

// Open payment modal
const openPaymentModal = () => {
  showModal.value = true
}

// Close payment modal
const closeModal = () => {
  showModal.value = false
}

// Initialize Stripe elements
const initializeStripeElements = async () => {
  try {
    if (!stripe) {
      // Load Stripe instance from promise
      stripe = await stripePromise
      
      if (!stripe) {
        throw new Error('Stripe failed to initialize')
      }
    }
    
    // Create elements instance
    elements = stripe.elements()
    
    // Mount the card element
    mountCardElement()
  } catch (error) {
    console.error('Error initializing Stripe elements:', error)
    cardError.value = error instanceof Error ? error.message : 'Stripe initialization failed'
  }
}

// Mount card element
const mountCardElement = () => {
  if (!elements) {
    console.error('Stripe Elements not initialized')
    return
  }

  try {
    // Create card element
    cardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: '#32325d',
          fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          '::placeholder': { color: '#aab7c4' }
        },
        invalid: {
          color: '#dc3545',
          iconColor: '#dc3545'
        }
      },
      hidePostalCode: true
    })

    // Mount to DOM
    const mountPoint = document.getElementById('stripe-card-element')
    if (mountPoint) {
      cardElement.mount('#stripe-card-element')

      // Listen for card input changes
      cardElement.on('change', (event) => {
        isCardComplete.value = event.complete
        cardError.value = event.error ? event.error.message : ''
      })
    } else {
      console.error('Card element mount point not found')
    }
  } catch (error) {
    console.error('Error mounting card element:', error)
    cardError.value = error instanceof Error ? error.message : 'Failed to load card input'
  }
}

// Unmount card element
const unmountCardElement = () => {
  if (cardElement) {
    cardElement.unmount()
    cardElement = null
  }
}

// Format number with commas
const numberWithCommas = (x: number): string => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

// Handle payment submission
const handlePaymentSubmission = async () => {
  if (!stripe || !cardElement || isProcessing.value) {
    return
  }

  isProcessing.value = true
  cardError.value = ''

  try {
    // Step 1: Create a PaymentMethod
    const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: props.title,
        email: props.mail
      }
    })

    if (paymentMethodError) {
      throw new Error(paymentMethodError.message)
    }

    // Validate premium service ID exists
    if (!props.premiumServiceId) {
      throw new Error('Missing premium service ID. Cannot proceed with payment.')
    }

    // Step 2: Create payment intent for premium service subscription
    const formData = {
      premium_service_id: props.premiumServiceId,
      price_plan_id: props.pricePlanId || "",
      coupon_code: props.couponCode || "",
      installment_count: props.installmentCount || "",
      amount: props.purchasePrice,
      payment_method_id: paymentMethod.id,
      action_type: "change_card",
      type_change: props.type_change || "",
      change_from_subscription_id: props.changeFromSubscriptionId || "",
      name: props.title || ""
    }

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    // Make the request with proper URL
    const response = await fetch(`/premium_services/${props.premiumServiceId}/subscriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Rails picks the response format from Accept, not from Content-Type.
        // fetch defaults to */*, which makes request.format.json? false: the
        // controller then skips its CSRF exemption and renders HTML error pages.
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken || ''
      },
      body: JSON.stringify(formData)
    })

    const data = await readJsonResponse(response)

    if (!response.ok) {
      throw new Error(data?.error || `サーバーエラーが発生しました (${response.status})。もう一度お試しください。`)
    }

    if (!data) {
      throw new Error('サーバーから予期しない応答が返されました。もう一度お試しください。')
    }

    // Step 3: Handle 3D Secure authentication if needed
    if (data.client_secret) {
      const { setupIntent, error } = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: paymentMethod.id
      })

      if (error) {
        cardError.value = error.message || ''
        return
      } else {
        cardBrand.value = paymentMethod.card?.brand || ''
        cardLast4.value = paymentMethod.card?.last4 || ''
        cardExpMonth.value = paymentMethod.card?.exp_month || null
        cardExpYear.value = paymentMethod.card?.exp_year || null
      }

      emit('payment-method-cart-setup', setupIntent?.payment_method as string || '')
      emit('client-secret-cart-setup', data.client_secret)
      emit('subscription-id-cart-setup', data.subscription_id)

      emit('card-details', {
        brand: cardBrand.value,
        last4: cardLast4.value,
        exp_month: cardExpMonth.value,
        exp_year: cardExpYear.value
      })

      // Payment successful after 3D Secure authentication
      paymentSuccessful(setupIntent?.id || '')
    } else if (data.success) {
      // No 3D Secure needed, payment was successful
      paymentSuccessful(data.subscription_id || data.payment_intent_id)
    } else if (data.error) {
      throw new Error(data.error)
    } else {
      paymentSuccessful(data.subscription_id)
    }
  } catch (error) {
    // Type check for error.message
    let errorMessage = 'お支払い処理中にエラーが発生しました。もう一度お試しください。'

    if (error instanceof Error) {
      errorMessage = error.message
    } else if (typeof error === 'object' && error !== null && 'message' in error) {
      errorMessage = (error as any).message
    }

    cardError.value = errorMessage
    isProcessing.value = false
  }
}

// Handle successful payment
const paymentSuccessful = (paymentIntentId: string) => {
  isProcessing.value = false
  showModal.value = false

  // Call the provided callback with the payment intent ID
  // props.cardComplete?.(paymentIntentId)
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createCommentVNode(" Card payment button "),
    _createVNode(_component_v_btn, {
      color: "primary",
      variant: "outlined",
      onClick: openPaymentModal
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(__props.btn), 1 /* TEXT */)
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" Stripe Modal "),
    _createVNode(_component_v_dialog, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
              default: _withCtx(() => [
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-h6" }, "クレジットカード情報", -1 /* CACHED */)),
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  icon: "mdi-close",
                  variant: "text",
                  onClick: closeModal
                })
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_form, {
                  ref: "form",
                  onSubmit: _withModifiers(handlePaymentSubmission, ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createCommentVNode(" Hidden field for purchase total "),
                    _createElementVNode("input", {
                      type: "hidden",
                      id: "modal-purchase-total",
                      value: __props.purchasePrice
                    }, null, 8 /* PROPS */, _hoisted_1),
                    _createCommentVNode(" Card Element "),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, {
                              class: "pa-2",
                              variant: "outlined"
                            }, {
                              default: _withCtx(() => [
                                _cache[2] || (_cache[2] = _createElementVNode("div", {
                                  id: "stripe-card-element",
                                  class: "stripe-element-container"
                                }, null, -1 /* CACHED */)),
                                (cardError.value)
                                  ? (_openBlock(), _createBlock(_component_v_alert, {
                                      key: 0,
                                      density: "compact",
                                      variant: "outlined",
                                      type: "error",
                                      class: "mt-2"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(cardError.value), 1 /* TEXT */)
                                      ]),
                                      _: 1 /* STABLE */
                                    }))
                                  : _createCommentVNode("v-if", true)
                              ]),
                              _: 1 /* STABLE */
                            })
                          ]),
                          _: 1 /* STABLE */
                        })
                      ]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 512 /* NEED_PATCH */)
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  loading: isProcessing.value,
                  disabled: isProcessing.value || !isCardComplete.value,
                  onClick: handlePaymentSubmission
                }, {
                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                    _createTextVNode(" 支払いを確定する ", -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["loading", "disabled"])
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["modelValue"])
  ]))
}
}

})